import React, { useRef, useState } from "react";
import { Flex, Radio } from "antd";
import InboxInvoiceFormDetails from "../InboxInvoiceFormDetails/InboxInvoiceFormDetails";
import InvoiceFormNote, {
  InvoiceFormNoteHandle,
} from "../InvoiceFormNote/InvoiceFormNote";
import InvoiceFormHistory from "../InvoiceFormHistory/InvoiceFormHistory";
// import InvoiceFormDetails from "../../../../Components/InvoiceFormDetails/InvoiceFormDetails";
import InvoiceEditor from "../../../../Components/InvoiceEditor";
import "./InvoiceForm.css";
import SetStatus from "../../../../Components/SetStatus/SetStatus";
import {
  GetInvoiceNoteService,
  UpdateInvoiceNoteService,
} from "../../../../Services/InvoiceService/InvoiceService";
import { useUnsavedChangesGuard } from "../../../../Components/StatementViewer/UnsavedChangesGuard";

interface InvoiceFormProps {
  setInvoiceView: any;
  invoiceDataSource: any;
  handleNextInvoice?: any;
  isLastInvoice?: boolean;
  onEdit?: () => void;
  refreshInvoices?: () => void;
}

const InvoiceForm = ({

  setInvoiceView,
  invoiceDataSource,
  handleNextInvoice,
  isLastInvoice,
  onEdit,
  refreshInvoices,
}: InvoiceFormProps) => {
  const [formType, setFormType] = useState("details");
  const { hasUnsavedChanges, guardNavigation } = useUnsavedChangesGuard();
  const stageId = invoiceDataSource?.[0]?.stageId;
  const invoiceItem = invoiceDataSource?.[0];
  const invoiceId = invoiceItem?.invoiceId;
  const [isNoteEdited, setIsNoteEdited] = useState(!!invoiceItem?.note?.trim());
  // Bumped whenever an action that should appear in the audit trail happens
  // (note save, edit, archive/unarchive) so the History tab refetches instead
  // of showing stale data. If the backend later pushes updates (polling,
  // websockets) this can be removed.
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  const noteTabRef = useRef<InvoiceFormNoteHandle>(null);

  const switchTab = (value: string) => {
    switch (value) {
      case "details":
        setFormType("details");
        break;
      case "note":
        setFormType("note");
        break;
      case "history":
        setFormType("history");
        break;
    }
  };

  const HandleSwitchBtnClick = (e: any) => {
    const nextValue = e.target.value;

    if (formType === "details" && hasUnsavedChanges) {
      guardNavigation(() => switchTab(nextValue));
      return;
    }

    if (formType === "note" && nextValue !== "note" && noteTabRef.current) {
      noteTabRef.current.guardNavigation(() => switchTab(nextValue));
      return;
    }

    switchTab(nextValue);
  };

  return (
    <div className="invoice-form">
      <div className="InvoiceForm-switchContainer">
        <Radio.Group
          defaultValue={"details"}
          value={formType}
          className="InvoiceForm-switch"
          buttonStyle="solid"
        >
          <Radio.Button
            onClick={(e) => HandleSwitchBtnClick(e)}
            value="details"
          >
            Details
          </Radio.Button>
          <Radio.Button onClick={(e) => HandleSwitchBtnClick(e)} value="note">
            Note
            {isNoteEdited && (
              <span className="InvoiceForm-editedBadge">Edited</span>
            )}
          </Radio.Button>
          <Radio.Button
            onClick={(e) => HandleSwitchBtnClick(e)}
            value="history"
          >
            History
          </Radio.Button>
        </Radio.Group>
        {stageId && (
          <SetStatus
            stageId={stageId}
            archived={invoiceDataSource?.[0]?.archiveStatus}
          />
        )}
      </div>

      {formType === "details" && (
        <InvoiceEditor
          setInvoiceView={setInvoiceView}
          invoiceData={invoiceDataSource}
          nextStage={21}
          handleNextInvoice={handleNextInvoice}
          isLastInvoice={isLastInvoice}
          onEdit={onEdit}
          // Any save inside InvoiceEditor should also log a FIELD_EDITED
          // entry on the backend — see BACKEND_API_CONTRACT.md. Once that
          // save resolves, bump historyRefreshKey so a user who flips to
          // History right after editing sees it immediately.
          refreshInvoices={() => {
            refreshInvoices?.();
            setHistoryRefreshKey((key) => key + 1);
          }}
        />
      )}

      <InvoiceFormNote
        ref={noteTabRef}
        itemId={invoiceId}
        currentUserName={"John Doe"} // Replace with currentUserName from your auth context
        onLoad={(id) => GetInvoiceNoteService(Number(id))}
        onSave={(id, note, rowVersion) =>
          UpdateInvoiceNoteService(Number(id), note, rowVersion)
        }
        onEditedStateChange={setIsNoteEdited}
        onHistoryEntry={() => setHistoryRefreshKey((key) => key + 1)}
        hidden={formType !== "note"}
      />

      {formType === "history" && (
        <InvoiceFormHistory key={historyRefreshKey} itemId={invoiceId} />
      )}
    </div>
  );
};

export default InvoiceForm;
